import { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { PlayPauseControls } from '@/libraries/design/components/play-pause-controls/src/play-pause-controls';
import { wacToken } from '@/libraries/design/foundations';

export type BlurredBackgroundVideoProps = {
	src: string;
	poster?: string;
	videoDescription?: string;
};

export const BlurredBackgroundVideo = ({
	src,
	poster,
	videoDescription,
}: BlurredBackgroundVideoProps) => {
	const backgroundVideoRef = useRef<HTMLVideoElement>(null);
	const foregroundVideoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	const syncBackgroundVideo = useCallback(() => {
		const backgroundVideo = backgroundVideoRef.current;
		const foregroundVideo = foregroundVideoRef.current;

		if (!backgroundVideo || !foregroundVideo) {
			return;
		}

		if (Math.abs(backgroundVideo.currentTime - foregroundVideo.currentTime) > 0.25) {
			backgroundVideo.currentTime = foregroundVideo.currentTime;
		}
	}, []);

	const pauseVideos = useCallback(() => {
		foregroundVideoRef.current?.pause();
		backgroundVideoRef.current?.pause();
		setIsPlaying(false);
	}, []);

	const playVideos = useCallback(() => {
		const foregroundVideo = foregroundVideoRef.current;
		const backgroundVideo = backgroundVideoRef.current;

		if (!foregroundVideo) {
			return;
		}

		syncBackgroundVideo();

		if (backgroundVideo) {
			void backgroundVideo.play().catch(() => undefined);
		}

		void foregroundVideo
			.play()
			.then(() => {
				setIsPlaying(true);
			})
			.catch(() => {
				backgroundVideo?.pause();
				setIsPlaying(false);
			});
	}, [syncBackgroundVideo]);

	const handleToggleVideoPlayback = useCallback(() => {
		if (isPlaying) {
			pauseVideos();
			return;
		}

		playVideos();
	}, [isPlaying, pauseVideos, playVideos]);

	const handleForegroundVideoPlay = useCallback(() => {
		const backgroundVideo = backgroundVideoRef.current;

		syncBackgroundVideo();

		if (backgroundVideo?.paused) {
			void backgroundVideo.play().catch(() => undefined);
		}

		setIsPlaying(true);
	}, [syncBackgroundVideo]);

	const handleForegroundVideoPause = useCallback(() => {
		backgroundVideoRef.current?.pause();
		setIsPlaying(false);
	}, []);

	useEffect(() => {
		const foregroundVideo = foregroundVideoRef.current;

		if (!foregroundVideo) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					foregroundVideo.currentTime = 0;

					if (backgroundVideoRef.current) {
						backgroundVideoRef.current.currentTime = 0;
					}

					playVideos();
					return;
				}

				pauseVideos();
			},
			{ threshold: 0.5 },
		);

		observer.observe(foregroundVideo);

		return () => {
			observer.disconnect();
		};
	}, [pauseVideos, playVideos]);

	return (
		<div css={videoWrapperStyles}>
			<video
				ref={backgroundVideoRef}
				src={src}
				playsInline
				muted
				preload="auto"
				poster={poster}
				loop
				aria-hidden="true"
				tabIndex={-1}
				css={backgroundVideoStyles}
			>
				<track kind="captions" />
			</video>
			<div css={foregroundVideoFrameStyles}>
				<video
					ref={foregroundVideoRef}
					src={src}
					playsInline
					muted
					preload="auto"
					poster={poster}
					loop
					css={foregroundVideoStyles}
					aria-label={videoDescription ?? ''}
					aria-hidden={!videoDescription}
					onPlay={handleForegroundVideoPlay}
					onPause={handleForegroundVideoPause}
					onEnded={handleForegroundVideoPause}
					onTimeUpdate={syncBackgroundVideo}
				>
					<track kind="captions" />
				</video>
				<div css={videoControlsStyles}>
					<PlayPauseControls
						isPlaying={isPlaying}
						onToggle={handleToggleVideoPlayback}
						mediaLabel={videoDescription}
					/>
				</div>
			</div>
		</div>
	);
};

const videoWrapperStyles = css({
	position: 'relative',
	display: 'grid',
	flexGrow: 1,
	alignSelf: 'stretch',
	justifySelf: 'stretch',
	overflow: 'hidden',
	containerType: 'size',
	backgroundColor: wacToken('color.neutral.light0'),
});

const backgroundVideoStyles = css({
	position: 'absolute',
	inset: token('space.0'),
	inlineSize: '100%',
	blockSize: '100%',
	border: 0,
	filter: 'blur(60px)',
	transform: 'scale(1.16)',
	objectFit: 'cover',
	pointerEvents: 'none',
});

const foregroundVideoFrameStyles = css({
	position: 'relative',
	zIndex: 1,
	placeSelf: 'center',
	aspectRatio: '16 / 9',
	inlineSize: 'min(100cqw, calc(100cqh * 16 / 9))',
	blockSize: 'min(100cqh, calc(100cqw * 9 / 16))',
});

const foregroundVideoStyles = css({
	display: 'block',
	inlineSize: '100%',
	blockSize: '100%',
	border: 0,
	backgroundColor: 'transparent',
	objectFit: 'contain',
});

const videoControlsStyles = css({
	position: 'absolute',
	insetBlockEnd: token('space.200'),
	insetInlineEnd: token('space.200'),
	zIndex: 2,
});

BlurredBackgroundVideo.displayName = 'BlurredBackgroundVideo';
